"use client";

import { useCallback } from "react";
import { useWalletActionGuard } from "./useWalletActionGuard";
import { useSignMessage } from "wagmi";

/**
 * Wrapper hook for wagmi's useSignMessage that ensures the wallet is connected before signing.
 *
 * Scenario: User is authenticated with Privy but their wallet got disconnected
 * (e.g., MetaMask disconnected, mobile in-app browser session dropped after phone sleep).
 * This hook will attempt to reconnect the user's previous wallet connector before signing
 * the message, and retry once if the connector is stale despite appearing connected.
 *
 * Uses wagmi's useReconnect (connector-agnostic) instead of hardcoding injected(),
 * so it works with MetaMask, WalletConnect, Rabby, and any other connector type.
 *
 * @returns {Object} Hook return value
 * @returns {Function} signMessageAsync - Signs a message, reconnecting wallet if needed. Returns null on rejection/error.
 * @returns {boolean} isSigning - Whether a signing operation is in progress
 */
export const useSignMessageWithConnection = () => {
  const { executeMainEvmAction } = useWalletActionGuard();
  const { signMessageAsync: wagmiSignMessageAsync, isPending: isSigning } = useSignMessage();

  const signMessageAsync = useCallback(
    async ({ message }: { message: string }): Promise<string> => {
      return executeMainEvmAction(() => wagmiSignMessageAsync({ message }), { retryOnConnectorError: true });
    },
    [executeMainEvmAction, wagmiSignMessageAsync],
  );

  return {
    signMessageAsync,
    isSigning,
  };
};
